/**
 * Position Sources layer — renders independent crosshair markers for each
 * position source (V1-DA GPS, V4 estimated, V5 segment-computed) on the
 * selected vehicle. Controlled by the "Position Sources" toggles in the
 * settings panel, independent of the existing debug overlay toggles.
 *
 * Requires authentication (any logged-in user).
 */

import { memo, useMemo, useRef, useState } from 'react';
import { Source, Layer, useMap } from 'react-map-gl/maplibre';
import type { CircleLayerSpecification, SymbolLayerSpecification } from 'maplibre-gl';
import type { FeatureCollection, Point } from 'geojson';

import { useVehicleStore } from '../../stores/vehicleStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useAuthStore } from '../../stores/authStore';
import { useSegmentStore } from '../../stores/segmentStore';
import { interpolateSegmentPosition } from '../../utils/segmentInterpolation';
import { CROSSHAIR_ICON } from '../../utils/vehicleIcons';
import { useAnimationLoop } from '../../hooks/useAnimationLoop';

// Source/layer IDs — must be unique across all map layers
const PS_V1DA_SOURCE = 'ps-v1da-source';
const PS_V1DA_BG_LAYER = 'ps-v1da-bg';
const PS_V1DA_ICON_LAYER = 'ps-v1da-icon';

const PS_V4_SOURCE = 'ps-v4-source';
const PS_V4_BG_LAYER = 'ps-v4-bg';
const PS_V4_ICON_LAYER = 'ps-v4-icon';

const PS_V5_SOURCE = 'ps-v5-source';
const PS_V5_BG_LAYER = 'ps-v5-bg';
const PS_V5_ICON_LAYER = 'ps-v5-icon';

/** Max age for segment data before hiding V5 marker (15 seconds) */
const SEGMENT_FRESHNESS_MS = 15_000;

interface PositionFeatureProperties {
  id: string;
  color_bg: string;
}

const EMPTY_GEOJSON: FeatureCollection<Point, PositionFeatureProperties> = {
  type: 'FeatureCollection',
  features: [],
};

function makeBackgroundStyle(
  id: string,
  source: string,
  strokeColor: string
): CircleLayerSpecification {
  return {
    id,
    type: 'circle',
    source,
    paint: {
      'circle-radius': 14,
      'circle-color': ['get', 'color_bg'],
      'circle-stroke-color': strokeColor,
      'circle-stroke-width': 2,
      'circle-stroke-opacity': 0.6,
      'circle-opacity': 0.3,
    },
  } as CircleLayerSpecification;
}

function makeIconStyle(
  id: string,
  source: string,
  iconColor: string
): SymbolLayerSpecification {
  return {
    id,
    type: 'symbol',
    source,
    layout: {
      'icon-image': CROSSHAIR_ICON,
      'icon-size': 2.0,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'icon-anchor': 'center',
    },
    paint: {
      'icon-color': iconColor,
      'icon-opacity': 0.9,
    },
  } as SymbolLayerSpecification;
}

export const PositionSourcesLayer = memo(function PositionSourcesLayer() {
  useMap();

  const isLoggedIn = useAuthStore((state) => !!state.user);
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const selectedVehicle = useVehicleStore((state) =>
    state.selectedVehicleId ? state.vehicles.get(state.selectedVehicleId) : null
  );
  const showV1DA = useSettingsStore((state) => state.showPositionV1DA);
  const showV4 = useSettingsStore((state) => state.showPositionV4);
  const showV5 = useSettingsStore((state) => state.showPositionV5);
  const segment = useSegmentStore((state) =>
    selectedVehicleId ? state.segments.get(selectedVehicleId) : undefined
  );

  // Animation loop for V5 (segment interpolation requires rAF)
  const [tick, setTick] = useState<[number, number]>(() => [performance.now(), Date.now()]);
  const lastTickRef = useRef(0);
  useAnimationLoop((timestamp) => {
    if (timestamp - lastTickRef.current > 32) { // ~30fps
      lastTickRef.current = timestamp;
      setTick([timestamp, Date.now()]);
    }
  }, isLoggedIn && showV5 && !!segment);

  const animNow = tick[0];
  const wallNow = tick[1];

  // V1-DA GPS: targetLatitude/targetLongitude (raw latest GPS coords)
  const v1daGeojson = useMemo(() => {
    if (!selectedVehicle || !showV1DA || !isLoggedIn) return EMPTY_GEOJSON;
    if (selectedVehicle.visualState === 'dead') return EMPTY_GEOJSON;
    // Only show if we actually have V1-DA data
    if (selectedVehicle.v1LastUpdateTime === null) return EMPTY_GEOJSON;

    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [selectedVehicle.targetLongitude, selectedVehicle.targetLatitude],
        },
        properties: {
          id: selectedVehicle.service_journey_id,
          color_bg: selectedVehicle.line?.color_bg || '#666666',
        },
      }],
    } as FeatureCollection<Point, PositionFeatureProperties>;
  }, [selectedVehicle, showV1DA, isLoggedIn]);

  // V4 Estimated: v4_position (secondary position when GPS is primary)
  const v4Geojson = useMemo(() => {
    if (!selectedVehicle || !showV4 || !isLoggedIn) return EMPTY_GEOJSON;
    if (selectedVehicle.visualState === 'dead') return EMPTY_GEOJSON;
    if (!selectedVehicle.v4_position) return EMPTY_GEOJSON;

    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [selectedVehicle.v4_position.longitude, selectedVehicle.v4_position.latitude],
        },
        properties: {
          id: selectedVehicle.service_journey_id,
          color_bg: selectedVehicle.line?.color_bg || '#666666',
        },
      }],
    } as FeatureCollection<Point, PositionFeatureProperties>;
  }, [selectedVehicle, showV4, isLoggedIn]);

  // V5 Computed: segment interpolation (animated)
  const v5Geojson = useMemo(() => {
    if (!selectedVehicle || !showV5 || !isLoggedIn || !segment) return EMPTY_GEOJSON;
    if (selectedVehicle.visualState === 'dead') return EMPTY_GEOJSON;
    if (wallNow - segment.receivedAt > SEGMENT_FRESHNESS_MS) return EMPTY_GEOJSON;

    const v5Pos = interpolateSegmentPosition(segment, animNow);
    if (!v5Pos) return EMPTY_GEOJSON;

    const [lat, lon] = v5Pos;
    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lon, lat],
        },
        properties: {
          id: selectedVehicle.service_journey_id,
          color_bg: segment.color_bg || selectedVehicle.line?.color_bg || '#666666',
        },
      }],
    } as FeatureCollection<Point, PositionFeatureProperties>;
  }, [selectedVehicle, showV5, isLoggedIn, segment, animNow, wallNow]);

  // Static layer styles (memoized once)
  const v1daBg = useMemo(() => makeBackgroundStyle(PS_V1DA_BG_LAYER, PS_V1DA_SOURCE, '#22c55e'), []);
  const v1daIcon = useMemo(() => makeIconStyle(PS_V1DA_ICON_LAYER, PS_V1DA_SOURCE, '#22c55e'), []);
  const v4Bg = useMemo(() => makeBackgroundStyle(PS_V4_BG_LAYER, PS_V4_SOURCE, '#FFD700'), []);
  const v4Icon = useMemo(() => makeIconStyle(PS_V4_ICON_LAYER, PS_V4_SOURCE, '#FFD700'), []);
  const v5Bg = useMemo(() => makeBackgroundStyle(PS_V5_BG_LAYER, PS_V5_SOURCE, '#7c3aed'), []);
  const v5Icon = useMemo(() => makeIconStyle(PS_V5_ICON_LAYER, PS_V5_SOURCE, '#a855f7'), []);

  // Early exit if nothing to render
  if (!isLoggedIn || !selectedVehicleId || (!showV1DA && !showV4 && !showV5)) {
    return null;
  }

  return (
    <>
      {showV1DA && (
        <Source id={PS_V1DA_SOURCE} type="geojson" data={v1daGeojson}>
          <Layer {...v1daBg} />
          <Layer {...v1daIcon} />
        </Source>
      )}
      {showV4 && (
        <Source id={PS_V4_SOURCE} type="geojson" data={v4Geojson}>
          <Layer {...v4Bg} />
          <Layer {...v4Icon} />
        </Source>
      )}
      {showV5 && (
        <Source id={PS_V5_SOURCE} type="geojson" data={v5Geojson}>
          <Layer {...v5Bg} />
          <Layer {...v5Icon} />
        </Source>
      )}
    </>
  );
});
